/**
 * A dialog for displaying help information for the system.
 */

const ID = 'help-modal';
const TEMPLATE = `
<div class="modal fade" id="${ID}" tabindex="-1" role="dialog" aria-labelledby="modal-title">
    <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">Help</h4>
            </div>
            <div class="modal-body">
                <h4>User guide Monitoring Site Planner</h4>
                <p>
                    The aim of the Monitoring Site Planner is to assist in the planning of coral reef monitoring on the 
                    Great Barrier Reef. This tool works by assessing a given monitoring design (reefs marked with pins) 
                    against the set of criteria chosen in the ‘Calculations’ tab. With these criteria the lower the 
                    score the better, where 0 represents a perfect score and 1 represents a poor score. A weight can be 
                    applied to the criteria that are of interest. Enabling a criterion will include it in the Combined 
                    score.
                </p>
                <p>
                    Some of the criteria assess the monitoring design on how well the environmental gradient variables 
                    (current, chlorophyll, NAP, secchi, temperature) can be reconstructed from the selected monitoring 
                    sites. The gradients are intended to act as proxies for differences in environmental conditions 
                    that the coral reefs grow in. These criteria help to assess how representative the monitoring 
                    design is of the different environmental conditions seen on the GBR. The NRM Region criteria simply 
                    assesses whether there is a fair number of sites in each NRM. The GBRMP Zones + Sub-Bioregions 
                    criteria assesses whether there are a fair number of match Blue/Green pairs of reefs within each 
                    sub bioregion, with a nominal maximum of 2 pairs per sub-bioregion. The Historic surveys criteria 
                    assesses what fraction of all the historic data exists at the sites selected.
                </p>
                <p>
                    The map panel in the tool allows monitoring sites to be added manually by clicking on specific 
                    reefs. Note that some reefs are not selectable because they were assessed as unsuitable for surveys 
                    due to excessive tidal currents, no reef top to survey or the perimeter of the reef is too large to 
                    manta tow. These reefs are however included in the assessment of the monitoring design. 
                </p>
                <p style="text-align: center">
                    <a href="resources/images/userguide.png" target="_blank" title="Open image in new tab">
                        <img style="max-width: 90%" src="resources/images/userguide.png" alt="user guide"/>
                     </a>
                </p>
                <p>
                    The map panel has a slider that shows on the left side the original dataset (as chosen in the top 
                    drop down) and the right side shows the reconstruction of the map based solely on the information 
                    provided by the selected monitoring sites. There is a slider at the bottom of the map to allow the 
                    comparison. If the monitoring design is extensive and well spread, then the reconstructed map will 
                    be close to the original proxy variable.
                </p>
                <p>
                    You can upload and download monitoring sites lists. These lists correspond to the GBRMPA reef IDs, 
                    one per line. Some example lists can be found in the following CSV file 
                    <a href="https://eatlas.org.au/pydio/public/msp-reef-listscsv" target="_blank" title="Example list">https://eatlas.org.au/pydio/public/msp-reef-listscsv</a>. 
                    It contains a list for the existing 2018 monitoring program, an adjustment made based on expert 
                    opinion and a list where the system optimised the sites based on the criteria. To use each list, 
                    copy the column of reef IDs into the upload dialog box. 
                </p>
                <p>
                    Limitations: The tool does not include the life history of coral reefs in its assessment of the 
                    monitoring program. The disturbance history of a coral reef is a very strong determinant of the 
                    status of the reef, this is not included in the tool. 
                </p>
                <p>
                    A small percentage of monitored reefs are not included in this tool because of the poor reef 
                    mapping in some areas of the GBR meant that there were not matching Reef IDs that we could find. 
                </p>
                <p>
                    This tool assesses the performance of the monitoring over all the reefs in the GBR. It was no 
                    designed to assess just a part of the reef. It you want to look at how to optimise say just the 
                    Cape York region then load in the existing monitoring program for the whole GBR then adjust just 
                    the Cape York region.
                </p>
                <p>
                    While the tool excludes some reefs from being selected for logistical reasons (reef perimeter, lack 
                    of reef top, reef currents), we later found out that sometimes these rules are not as fixed as we 
                    included, therefore the tool excludes some reefs that should have been included. Also the tool does 
                    not consider risk from crocodiles and thus included a lot of inshore northern reefs that would 
                    generally not be considered as viable for monitoring using existing techniques.
                </p>
                <p>
                    The GBRMP Zones + Sub-bioregions criteria will generally give a valid but poor score relative to the 
                    other criteria, because it expects two matched reef pairs per sub-bioregion. Since there are 70 
                    sub-bioregions this would need a monitoring program of at least 280 reefs to get a perfect score. 
                    Ideally this calculation should have been performed on larger clusters of reefs, using bioregions 
                    instead of sub-bioregions.
                </p>
                <p>
                    <strong>More information:</strong> can be found in the report for the Monitoring Site Planner 
                    <a href="https://eatlas.org.au/pydio/public/monitoring-site-planner-reportmaster2019-02-16pdf" target="_blank" title="Monitoring Site Planner report">https://eatlas.org.au/pydio/public/monitoring-site-planner-reportmaster2019-02-16pdf</a>.
                </p>
                
                <h4>Map</h4>
                <p>Hold the &lt;SHIFT&gt; key when highlighting an area on the map to zoom in to that area.</p>
                <p>Hold the &lt;CTRL&gt; key when highlighting features on a map to select those features. 
                    The chart will update with data for those features only. Click anywhere on the map to
                    deselect those features, and the chart will update to show data for ALL features.</p>
                <p>Click the layer (<i class="material-icons md-24">layers</i>) icon to open the popup for
                    adding layers to the map. Click the icon again to close the popup.</p>
                    
                <h4>Chart</h4>
                <p>Select along the x-axis to filter the data. The map will update to show only those features
                    with data available for the selected date range. Click outside of this selection on the
                    x-axis to deselect the date range, and the map will update to show all features again.</p>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-default" data-dismiss="modal">Close</button>
            </div>
        </div>
    </div>
</div>
`;

export default class HelpDialog {


  /**
   * Constructor to capture the reference to the triggering button.
   */
  constructor($button) {
    $button.click(function() {
      this._render();
    }.bind(this));
  }

  /**
   * Render the modal for the user to make a selection.
   */
  _render() {

    // Remove any previous instances of the modal from the body.
    $(`#${ID}`).remove();

    // Append the template afresh to the body and capture the reference.
    $('body').append(TEMPLATE);
    const $modal = $(`#${ID}`);

    // Display the modal.
    $modal.modal('show');

  }

}



// WEBPACK FOOTER //
// ./src/app/components/dialogs/help-dialog.js